import type {
  AccountRef, Anchor, AssessRequest, ChangeSent, ChangeSummary, Minor, NotaDto, LevelRef, OwnChangeRequest, PersonDto
} from "@nabla/api";
import type { AskRequest, CommandBody, ReplyRequest, RoleRequest, SessionView, SettingEdit } from "@nabla/api";
import type { ExceptionView, ThreadSummary } from "@nabla/api";
import type { Core } from "./client.ts";
import { decidedByAnother, type Outcome, type Settled, type Taken } from "./commands.ts";
import { clientCopy } from "./copy.ts";
import type { ClientError } from "./http.ts";

type AdAccountId = AccountRef["id"];
type AssessmentVerdict = AssessRequest["verdict"];
type PersonId = PersonDto["id"];
type Role = RoleRequest["role"];
type ThreadId = ThreadSummary["id"];
type NotaId = NotaDto["id"];

const objectsOfChange = (change: ChangeSummary) => [`mudanca:${change.id}`, `${change.target.place}:${change.target.id}`, `conta:${change.account.id}`, ...change.path.map((ref) => `${ref.place}:${ref.id}`)];

function notaActions({ commands, views }: Core) {
  const reread = () => views.invalidate([], ["c"]);
  return {
    saveNota(thread: ThreadId, seq: number, text: string) {
      const effect = (answer: { readonly nota: NotaDto } | null) => answer && views.invalidate([`${answer.nota.anchor.place}:${answer.nota.anchor.id}`, `conversa:${thread}`], ["c"]);
      return commands.send("guardarNota", { body: { thread, seq, text }, target: `conversa:${thread}`, effect });
    },
    renameNota(id: string, title: string) {
      return commands.send("renomearNota", { params: { n: id }, body: { title }, target: `nome-da-nota:${id}`, effect: reread });
    },
    retireNota(id: string) {
      return commands.send("retirarNota", { params: { n: id }, body: {}, target: `nota:${id}`, effect: reread });
    },
  };
}

const changedIn = (core: Core) => (answer: { readonly change: ChangeSummary } | null) => {
  if (!answer) return;
  core.putChange(answer.change);
  core.views.invalidate(objectsOfChange(answer.change), ["r"]);
};

type Held = Pick<LevelRef, "place" | "id">;

function overrideActions(core: Core) {
  const { commands } = core;
  const changed = changedIn(core);
  return {
    change({ place, id }: Held, change: OwnChangeRequest["change"], reason: string, taken?: Taken) {
      return commands.send("mudar", { body: { target: { place, id }, change, reason }, target: `objeto:${id}`, effect: changed, taken });
    },
    revert(change: ChangeSummary, reason: string, hold: boolean, taken?: Taken) {
      const about = { params: { p: change.id }, target: `mudanca:${change.id}`, verb: "desfazer" };
      return commands.send("desfazer", { ...about, body: { reason, hold }, effect: changed, taken });
    },
    cancelApproval(change: ChangeSummary, reason: string) {
      const about = { params: { p: change.id }, target: `mudanca:${change.id}`, verb: "cancelar" };
      return commands.send("cancelar", { ...about, body: { revision: change.revision, reason }, effect: changed });
    },
  };
}

function writtenActions({ commands, views }: Core) {
  const reread = () => views.invalidate("todos", ["r"]);
  const comparison = (name: "comecarComparacao" | "encerrarComparacao") => (id: string) =>
    commands.send(name, { params: { c: id }, body: {}, target: `comparacao:${id}`, effect: reread });
  return {
    makeRule(body: CommandBody<"escreverRegra">) {
      return commands.send("escreverRegra", { body, target: body.replaces ? `regra:${body.replaces}` : "regra-nova", effect: reread });
    },
    retireRule(id: string, reason?: string) {
      const body = reason === undefined ? {} : { reason };
      return commands.send("retirarRegra", { params: { r: id }, body, target: `regra:${id}`, effect: reread });
    },
    declareComparison(content: CommandBody<"declararComparacao">["content"]) {
      return commands.send("declararComparacao", { body: { content }, target: "comparacao-nova", effect: reread });
    },
    startComparison: comparison("comecarComparacao"),
    closeComparison: comparison("encerrarComparacao"),
  };
}

function peopleActions(core: Core) {
  const { commands, views } = core;
  const reread = () => views.invalidate([], ["p"]);
  const personChanged = (answer: { readonly person: PersonDto } | null) => {
    if (answer) core.putPerson(answer.person);
    reread();
  };
  return {
    invite(role: Role, name?: string) {
      const body = { role, ...(name ? { name } : {}) };
      return commands.send("convidar", { body, target: "convites", effect: reread, toast: false });
    },
    cancelInvite(id: string) {
      return commands.send("cancelarConvite", { params: { id }, body: {}, target: `convite:${id}`, effect: reread });
    },
    setRole(id: PersonId, role: Role) {
      return commands.send("mudarPapel", { params: { id }, body: { role }, target: `pessoa:${id}`, effect: personChanged });
    },
    allowRules(id: PersonId, allowed: boolean) {
      return commands.send("permitirRegras", { params: { id }, body: { allowed }, target: `pessoa:${id}`, effect: personChanged });
    },
    endSessions(id: PersonId) {
      return commands.send("encerrarSessoes", { params: { id }, body: {}, target: `pessoa:${id}`, effect: reread });
    },
    disable(id: PersonId) {
      return commands.send("desativar", { params: { id }, body: {}, target: `pessoa:${id}`, effect: personChanged });
    },
  };
}

function scopeActions({ commands, views }: Core) {
  return {
    changeScope(account: AdAccountId, campaign: string, to: "dentro" | "fora", reason: string, toast: string) {
      const effect = () => views.invalidate("todos", ["r"]);
      const params = { act: account, id: campaign };
      return commands.send("escopo", { params, body: { to, reason }, target: `escopo:${campaign}`, effect, toast });
    },
  };
}

function sessionActions(core: Core) {
  const { commands } = core;
  const signedIn = (answer: SessionView | null) => answer && core.signedIn(answer);
  return {
    signInLink() {
      return commands.send("linkDeEntrada", { body: {}, target: "sessao", effect: () => undefined, toast: false });
    },
    signOut() {
      return commands.send("sair", { body: {}, target: "sessao", effect: () => core.signedOut("saiu"), toast: false });
    },
    acceptInvite(token: string, name: string) {
      return commands.send("aceitarConvite", { body: { token, name }, target: "sessao", effect: signedIn, toast: false });
    },
    signIn(token: string) {
      return commands.send("entrar", { body: { token }, target: "sessao", effect: signedIn, toast: false });
    },
  };
}

function ruleActions({ commands, views }: Core) {
  const about = (account: AdAccountId, toast: string) => ({ target: `regras:${account}`, toast });
  return {
    saveSetting(
      account: AdAccountId, expectedRevision: number, change: SettingEdit, reason: string | undefined, toast: string, shownIn?: ThreadId,
    ) {
      const settled = ({ state }: Settled) => {
        if (state === "falhou") return;
        views.invalidate([`conta:${account}`], ["r"]);
        if (shownIn !== undefined) views.invalidate([`conversa:${shownIn}`], ["c"]);
      };
      const body = { expectedRevision, change, ...(reason === undefined ? {} : { reason }) };
      return commands.send("mudarRegra", { ...about(account, toast), settled, body });
    },
    stop(account: AdAccountId, expectedRevision: number, reason: string, toast: string) {
      const effect = () => views.invalidate("todos", ["r"]);
      return commands.send("parar", { ...about(account, toast), effect, params: { act: account }, body: { expectedRevision, reason } });
    },
  };
}

type Granted = NonNullable<ExceptionView["excecao"]>;

const unavailable = (error: ClientError) => error.kind === "api" && error.code === "conversa-indisponivel";

export function createActions(core: Core) {
  const { commands, views } = core;
  const changed = changedIn(core);
  const seenOf = ({ revision, change, mode }: ChangeSummary): { revision: number; change: ChangeSent; mode: ChangeSummary["mode"] } =>
    ({ revision, change, mode });
  const deciding = (change: ChangeSummary, verb: "aprovar" | "recusar" | "ajustar", held: boolean) => ({
    params: { p: change.id }, target: `mudanca:${change.id}`, verb, effect: changed, lost: decidedByAnother(change.id, core.me),
    toast: change.mode === "shadow" ? clientCopy.registradoEmSombra : undefined, held: held ? clientCopy.segurando[verb] : undefined,
  });
  const onChange = (change: ChangeSummary, target: string) => ({ params: { p: change.id }, body: {}, target: `${target}:${change.id}`, effect: changed });
  const asked = (answer: { readonly thread: ThreadSummary } | null) => {
    if (answer) core.putThread(answer.thread);
    core.places.reread();
  };

  const approve = (change: ChangeSummary, held = false, excecao?: Granted): Promise<Outcome<unknown>> => {
    const body = { verdict: "approve", seen: seenOf(change), ...(excecao && { excecao }) } as const;
    return commands.send("decidir", { ...deciding(change, "aprovar", held), body });
  };

  return {
    approve,
    async approveAll(changes: readonly ChangeSummary[], held: string): Promise<number> {
      if (!(await commands.hold(held))) return 0;
      let missed = 0;
      for (const change of changes) if (!(await approve(change)).ok) missed += 1;
      return missed;
    },
    reject(change: ChangeSummary, reason: string, held = false): Promise<Outcome<unknown>> {
      return commands.send("decidir", { ...deciding(change, "recusar", held), body: { verdict: "reject", seen: seenOf(change), reason } });
    },
    adjust(change: ChangeSummary, to: Minor, reason: string, held = false, excecao?: Granted) {
      const body = { seen: seenOf(change), to, reason, ...(excecao && { excecao }) };
      return commands.send("ajustar", { ...deciding(change, "ajustar", held), body });
    },
    assess(change: ChangeSummary, verdict: AssessmentVerdict, note?: string) {
      return commands.send("avaliar", { ...onChange(change, "avaliacao"), body: { verdict, ...(note ? { note } : {}) } });
    },
    rereview: (change: ChangeSummary) => commands.send("revisar", onChange(change, "revisao")),
    reconcile(change: ChangeSummary) {
      return commands.send("conferir", onChange(change, "conferir"));
    },
    refresh() {
      return commands.send("atualizar", { body: {}, target: "atualizar", effect: () => views.invalidate("todos", ["a"]) });
    },
    ask(anchor: Anchor, text: string, again?: NotaId, drafted?: Pick<AskRequest, "thread" | "attached">) {
      const body = { anchor, text, ...(again === undefined ? {} : { again }), ...drafted };
      return commands.send("perguntar", { body, target: `ancora:${anchor.place}:${anchor.id}`, effect: asked, toast: false, taken: unavailable });
    },
    reply(thread: ThreadId, text: string, attached?: ReplyRequest["attached"]) {
      return commands.send("responder", { params: { c: thread }, body: { text, attached }, target: `conversa:${thread}`, effect: asked, toast: false, taken: unavailable });
    },
    ...notaActions(core), ...overrideActions(core), ...writtenActions(core), ...peopleActions(core),
    ...sessionActions(core), ...scopeActions(core), ...ruleActions(core),
  };
}

export type Actions = ReturnType<typeof createActions>;
